import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import './Header.css';

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile drawer and dropdown on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setShowUserDropdown(false);
  }, [location.pathname]);

  const handleNavClick = (anchorId) => {
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      navigate(`/${anchorId}`);
    } else {
      const el = document.querySelector(anchorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLogout = async () => {
    setShowUserDropdown(false);
    await logout();
    window.location.href = '/login';
  };

  return (
    <>
      <header className={`site-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="header-container">
          <Link to="/" className="brand-logo-link" aria-label="Durbhasi Gurukulam Home">
            <img
              src="/durbhasi-gurukulam-logo.png"
              alt="Durbhasi Gurukulam Emblem"
              className="brand-logo-img"
            />
            <div className="brand-text">
              <span className="brand-title">
                Durbhasi <span className="gurukulam-highlight">Gurukulam</span>
              </span>
              <span className="brand-tagline">Systems &amp; Software Engineering</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav aria-label="Main Navigation">
            <ul className="nav-menu">
              <li>
                <Link
                  to="/courses"
                  className={`nav-link ${location.pathname === '/courses' ? 'active' : ''}`}
                >
                  Courses
                </Link>
              </li>
              <li>
                <a
                  href="#pedagogy"
                  onClick={(e) => { e.preventDefault(); handleNavClick('#pedagogy'); }}
                  className="nav-link"
                >
                  Pedagogy
                </a>
              </li>
              <li>
                <a
                  href="#roadmaps"
                  onClick={(e) => { e.preventDefault(); handleNavClick('#roadmaps'); }}
                  className="nav-link"
                >
                  Career Tracks
                </a>
              </li>
              {/* <li>
                <a
                  href="#mentors"
                  onClick={(e) => { e.preventDefault(); handleNavClick('#mentors'); }}
                  className="nav-link"
                >
                  Faculty
                </a>
              </li> */}
              <li>
                <Link
                  to="/support"
                  className={`nav-link ${location.pathname === '/support' ? 'active' : ''}`}
                >
                  Quick Support
                </Link>
              </li>
              <li>
                <a
                  href="#contact"
                  onClick={(e) => { e.preventDefault(); handleNavClick('#contact'); }}
                  className="nav-link"
                >
                  Contact
                </a>
              </li>
            </ul>
          </nav>

          {/* Actions */}
          <div className="header-actions">
            <button
              type="button"
              className="theme-toggle-btn"
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              title={isDark ? 'Light Mode' : 'Dark Mode'}
            >
              {isDark ? (
                <svg className="theme-toggle-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"></circle>
                  <line x1="12" y1="1" x2="12" y2="3"></line>
                  <line x1="12" y1="21" x2="12" y2="23"></line>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                  <line x1="1" y1="12" x2="3" y2="12"></line>
                  <line x1="21" y1="12" x2="23" y2="12"></line>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                </svg>
              ) : (
                <svg className="theme-toggle-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                </svg>
              )}
            </button>

            {isAuthenticated ? (
              <div className="header-user-menu" ref={userMenuRef}>
                <button
                  type="button"
                  className="header-user-btn"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  aria-expanded={showUserDropdown}
                >
                  <span className="user-avatar-badge">
                    {user?.name ? user.name.charAt(0).toUpperCase() : (user?.username || user?.email || 'U').charAt(0).toUpperCase()}
                  </span>
                  <span className="user-name-text">
                    {user?.name ? user.name.split(' ')[0] : (user?.username || 'Student')}
                  </span>
                  <span className="user-dropdown-arrow">{showUserDropdown ? '▲' : '▼'}</span>
                </button>

                {showUserDropdown && (
                  <div className="header-user-dropdown">
                    <div className="dropdown-user-header">
                      <strong>{user?.name || user?.username || 'Student'}</strong>
                      <span>{user?.email}</span>
                      <span className="dropdown-role-badge">
                        {user?.role === 'admin' ? '🛡️ Admin' : '🎓 Student'}
                      </span>
                    </div>
                    <div className="dropdown-divider"></div>
                    <Link
                      to="/my-courses"
                      className="dropdown-item"
                      onClick={() => setShowUserDropdown(false)}
                    >
                      📚 My Courses &amp; Hubs
                    </Link>
                    <Link
                      to="/support"
                      className="dropdown-item"
                      onClick={() => setShowUserDropdown(false)}
                    >
                      🎧 Helpdesk &amp; Support
                    </Link>
                    <div className="dropdown-divider"></div>
                    <button
                      type="button"
                      className="dropdown-item logout-btn"
                      onClick={handleLogout}
                    >
                      🚪 Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => navigate('/login')}
              >
                Student Login
              </button>
            )}

            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => navigate('/courses')}
            >
              Enroll Now
            </button>

            <button
              type="button"
              className="mobile-menu-toggle-btn"
              id="mobile-menu-toggle"
              aria-label="Toggle navigation menu"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Drawer Backdrop */}
      <div
        className={`mobile-drawer-backdrop ${isMenuOpen ? 'open' : ''}`}
        id="mobile-drawer-backdrop"
        onClick={() => setIsMenuOpen(false)}
      ></div>

      {/* Mobile Drawer */}
      <aside className={`mobile-drawer ${isMenuOpen ? 'open' : ''}`} id="mobile-drawer" aria-label="Mobile Navigation">
        <div className="mobile-drawer-header">
          <div className="brand-text">
            <span className="brand-title">
              Durbhasi <span className="gurukulam-highlight">Gurukulam</span>
            </span>
            <span className="brand-tagline">Systems &amp; Software Engineering</span>
          </div>
          <div className="mobile-drawer-header-actions">
            <button
              type="button"
              className="theme-toggle-btn mobile-theme-toggle"
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              title={isDark ? 'Light Mode' : 'Dark Mode'}
            >
              {isDark ? (
                <svg className="theme-toggle-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"></circle>
                  <line x1="12" y1="1" x2="12" y2="3"></line>
                  <line x1="12" y1="21" x2="12" y2="23"></line>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                  <line x1="1" y1="12" x2="3" y2="12"></line>
                  <line x1="21" y1="12" x2="23" y2="12"></line>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                </svg>
              ) : (
                <svg className="theme-toggle-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                </svg>
              )}
            </button>
            <button
              type="button"
              className="modal-close-btn"
              id="mobile-drawer-close"
              aria-label="Close menu"
              onClick={() => setIsMenuOpen(false)}
            >
              &times;
            </button>
          </div>
        </div>

        <ul className="mobile-drawer-links">
          <li>
            <Link to="/courses" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
              Explore Courses
            </Link>
          </li>
          <li>
            <a
              href="#pedagogy"
              className="mobile-nav-link"
              onClick={(e) => { e.preventDefault(); handleNavClick('#pedagogy'); }}
            >
              The Gurukulam Method
            </a>
          </li>
          <li>
            <a
              href="#roadmaps"
              className="mobile-nav-link"
              onClick={(e) => { e.preventDefault(); handleNavClick('#roadmaps'); }}
            >
              Career Roadmaps
            </a>
          </li>
          {/* <li>
            <a
              href="#mentors"
              className="mobile-nav-link"
              onClick={(e) => { e.preventDefault(); handleNavClick('#mentors'); }}
            >
              Faculty &amp; Mentors
            </a>
          </li> */}
          <li>
            <Link to="/support" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
              Support &amp; Campus
            </Link>
          </li>
          <li>
            <a
              href="#contact"
              className="mobile-nav-link"
              onClick={(e) => { e.preventDefault(); handleNavClick('#contact'); }}
            >
              Contact Desk
            </a>
          </li>
        </ul>

        <div className="mobile-drawer-actions">
          {isAuthenticated ? (
            <>
              <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '8px' }}>
                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem' }}>
                  {user?.name || user?.username || 'Student'}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{user?.email}</div>
                <span className="dropdown-role-badge" style={{ marginTop: '4px' }}>
                  {user?.role === 'admin' ? '🛡️ Admin' : '🎓 Student'}
                </span>
              </div>
              <button
                type="button"
                className="btn btn-primary btn-md"
                style={{ marginBottom: '8px' }}
                onClick={() => { setIsMenuOpen(false); navigate('/my-courses'); }}
              >
                📚 My Courses &amp; Hubs
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={handleLogout}
              >
                Logout
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-md"
                style={{ marginTop: '8px' }}
                onClick={() => { setIsMenuOpen(false); navigate('/courses'); }}
              >
                Browse All Cohorts
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={() => { setIsMenuOpen(false); navigate('/login'); }}
              >
                Student Login
              </button>
              <button
                type="button"
                className="btn btn-primary btn-md"
                onClick={() => { setIsMenuOpen(false); navigate('/courses'); }}
              >
                Apply for Cohort
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

export default Header;
